// core/image-fade.js

/**
 * @module image-fade
 * Gracefully fades images in once they finish loading, so the empty / partial
 * "broken image" state is never shown. This matters most with the wsrv.nl
 * proxy (first-request latency while it fetches + converts to WebP) and now
 * that the full-screen page loader is hidden on production.
 *
 * Behaviour:
 *  - Only images that are STILL LOADING when first seen are hidden and faded
 *    in on their `load` event. Images already complete (cached) are left at
 *    their natural opacity, so there is no flash-to-zero on repeat views.
 *  - `error` also reveals the image, so a genuinely broken/alt image is shown
 *    rather than left permanently invisible.
 *  - A single MutationObserver picks up images added later (Barba transitions,
 *    Ajax Load More, slideshows), so no per-component wiring is required.
 *
 * Pairs with the `img.img-fade` / `img.img-fade.is-loaded` rules in
 * sass/screen/base/utils/_base.scss.
 */

const FADE_CLASS = 'img-fade';
const LOADED_CLASS = 'is-loaded';

let observer = null;

/**
 * Whether an image should be left alone by the fade system.
 * @param {HTMLImageElement} img
 * @return {boolean}
 */
function shouldSkip(img) {
    return (
        img.dataset.fadeBound === '1' ||          // already handled
        img.hasAttribute('data-no-fade') ||       // explicit opt-out
        !!img.closest('#zoom-image-dialog')       // dialog manages its own opacity
    );
}

/**
 * Hide a still-loading image and reveal it once loaded (or errored).
 * @param {HTMLImageElement} img
 * @return {void}
 */
function bindImage(img) {
    if (shouldSkip(img)) return;
    img.dataset.fadeBound = '1';

    // `complete` is true for both a finished load and an already-failed/empty
    // src. In either case nothing more will fire, so leave it at natural
    // opacity — never hide it (that would strand broken/cached images).
    if (img.complete) return;

    img.classList.add(FADE_CLASS);

    const reveal = () => {
        img.classList.add(LOADED_CLASS);
        img.removeEventListener('load', reveal);
        img.removeEventListener('error', reveal);
    };
    img.addEventListener('load', reveal);
    img.addEventListener('error', reveal);
}

/**
 * Process an <img>, or all <img> descendants of a container/node.
 * @param {Node} root
 * @return {void}
 */
function scan(root) {
    if (!root) return;
    if (root.tagName === 'IMG') { bindImage(root); return; }
    if (typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('img').forEach(bindImage);
}

/**
 * Initialise graceful image fade-in. Scans existing images and, once, starts a
 * MutationObserver so dynamically inserted images are handled too. Safe to call
 * multiple times — the observer is only created on the first call.
 * @param {Element|Document} [root=document] Scope to scan immediately.
 * @return {void}
 */
export function initializeImageFadeIn(root = document) {
    scan(root);

    if (!observer && 'MutationObserver' in window && document.body) {
        observer = new MutationObserver((mutations) => {
            mutations.forEach((m) => {
                m.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) scan(node);
                });
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }
}
